import { isSunoAudioUrl } from "@shared/suno";

export function getSunoLinkError(url: string): string | null {
  if (!isSunoAudioUrl(url)) return null;
  const path = new URL(url).pathname;
  const id = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
  return new RegExp(
    `^/(?:song/${id}/?|s/[a-z0-9]+/?|${id}\\.(?:mp3|mp4|m4a))$`,
    "i",
  ).test(path)
    ? null
    : "Paste a link to a specific Suno song, or download its audio and upload the file here.";
}

/** Coalesce double clicks and require an explicit retry after a failed link.
 * Scoped to the composer: no permanent block and no shared user data. */
export function createSunoImportGuard<T>() {
  const pending = new Map<string, Promise<T>>();
  const failures = new Map<string, unknown>();
  return {
    retry(url: string) {
      failures.delete(url);
    },
    async run(url: string, fetchAudio: () => Promise<T>): Promise<T> {
      if (!isSunoAudioUrl(url)) return fetchAudio();
      const invalid = getSunoLinkError(url);
      if (invalid) throw new Error(invalid);
      if (failures.has(url)) throw failures.get(url);
      const existing = pending.get(url);
      if (existing) return existing;
      const request = Promise.resolve()
        .then(fetchAudio)
        .catch((error) => {
          if (failures.size >= 20)
            failures.delete(failures.keys().next().value!);
          failures.set(url, error);
          throw error;
        })
        .finally(() => pending.delete(url));
      pending.set(url, request);
      return request;
    },
  };
}
